import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { trackClick } from 'js/utils/trackClick';

import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';

const Container = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: var(--spacing-xs-16, 16px) 14px;
  padding-top: calc(var(--spacing-xs-16, 16px) + env(safe-area-inset-top, 0px));
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  border-bottom: 1px solid var(--stroke-grey-light, #dddde2);
  background: #fff;

  &.hidden {
    display: none;
  }
`;

const TitleRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary);
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-weight: 400;
  font-size: 20px;
  line-height: var(--type-sans-serif-sm-line-height, 24px);
`;

const InfoBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  height: 28px;
  background-color: var(--brand-colors-neutral-300, #ebebee);
  border-radius: 8px;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--brand-colors-neutral-400, #d9d9dd);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-colors-lime-500, #8cb369);
    outline-offset: 2px;
  }

  .material-symbols-outlined {
    color: var(--brand-colors-expanded-colors-lime-900, #333e2c);
  }
`;

const InfoBadgeText = styled(Body)`
  color: var(--brand-colors-expanded-colors-lime-900, #333e2c);
`;

interface Props {
  workTitle: string;
  workId: number;
  designVariant: string;
}

export const StickyWorkCardHeader: React.FC<Props> = ({
  workTitle,
  workId,
  designVariant,
}) => {
  const [hasScrolledPastDocumentTop, setHasScrolledPastDocumentTop] =
    useState(false);
  const [isDocumentBottomInView, setIsDocumentBottomInView] = useState(false);
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'large';

  // Show header once the top of the viewport has passed the top of the document;
  // hide when the bottom of the document is in view
  useEffect(() => {
    const embeddedDocument = document.querySelector(
      '.js-loswp-embedded-document',
    ) as HTMLElement;
    if (!embeddedDocument) return;

    const checkDocumentPosition = () => {
      const rect = embeddedDocument.getBoundingClientRect();
      setHasScrolledPastDocumentTop(rect.top <= 0);
      setIsDocumentBottomInView(rect.bottom <= window.innerHeight);
    };

    window.addEventListener('scroll', checkDocumentPosition);
    checkDocumentPosition();

    return () => window.removeEventListener('scroll', checkDocumentPosition);
  }, []);

  const handleInfoBadgeClick = () => {
    trackClick({
      source: 'loswp--info-badge--sticky-header',
      bucket: designVariant,
      entityId: workId,
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Expand all content controlled by the info badge (metadata + content sections)
    const workCardInfoBadge = document.querySelector(
      '.js-info-badge-toggle',
    ) as HTMLButtonElement;
    const collapsibles = document.querySelectorAll<HTMLElement>(
      '.js-info-badge-collapsible',
    );

    if (workCardInfoBadge) {
      workCardInfoBadge.setAttribute('aria-expanded', 'true');
    }
    collapsibles.forEach((el) => {
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('expanded');
    });
  };

  const shouldShow =
    hasScrolledPastDocumentTop && !isDesktop && !isDocumentBottomInView;

  return (
    <Container className={shouldShow ? '' : 'hidden'} aria-hidden="true">
      <TitleRow>
        <Title>{workTitle}</Title>
        <InfoBadge
          type="button"
          aria-label="Scroll to work details"
          onClick={handleInfoBadgeClick}
        >
          <Icon name="info" size={16} />
          <InfoBadgeText size="sm">Info</InfoBadgeText>
          <Icon name="keyboard_arrow_up" size={16} />
        </InfoBadge>
      </TitleRow>
    </Container>
  );
};

export default StickyWorkCardHeader;
